import React, { useCallback, useEffect, useState } from 'react';
import { Controller, type Control } from 'react-hook-form';
import { Platform } from 'react-native';
import { KeyboardAwareScrollView } from 'react-native-keyboard-controller';
import { useShallow } from 'zustand/shallow';

import { Stack } from '@/components/layout';
import { Button, FormInput, SheetContent, SheetModal, showAlert, Text } from '@/components/ui';
import { STRINGS } from '@/constants/strings';
import { useGuardianReportIssueForm } from '@/features/guardian-profile/hooks/use-guardian-report-issue-form';
import { useReportAttachmentPicker } from '@/features/guardian-profile/hooks/use-report-attachment-picker';
import {
  getReportAttachmentPickerOptions,
  getReportAttachmentValidationIssue,
  MAX_REPORT_DESCRIPTION_LENGTH,
  MAX_REPORT_ISSUE_LENGTH,
  mergeReportAttachments,
  type GuardianReportIssueForm,
  type ReportAttachment,
} from '@/features/guardian-profile/schemas/guardian-report-issue-schema';
import { createStyles, useScaling } from '@/hooks';
import { uploadLogs } from '@/libs/debug-log';
import { uploadFileToStorage } from '@/libs/firebase/storage/storage-helpers';
import { useSubmitBugReportMutation } from '@/queries/use-submit-bug-report-mutation';
import { useUiStore } from '@/stores';
import { useSessionStore } from '@/stores/session-store';
import { guardianColors } from '@/theme/colors';
import { MAX_EMAIL_LENGTH } from '@/validation';

import { ReportAttachmentField } from './report-attachment-field';
import { ReportAttachmentPreviewModal } from './report-attachment-preview-modal';

const strings = STRINGS.guardian.reportIssue;

function attachmentErrorMessage(
  issue: ReturnType<typeof getReportAttachmentValidationIssue>,
): string | null {
  if (issue === 'oversize-image') return strings.uploadSizeErrorImage;
  if (issue === 'oversize-video') return strings.uploadSizeErrorVideo;
  if (issue === 'oversize-both') return strings.uploadSizeErrorBoth;
  if (issue === 'limit') return strings.uploadLimitMessage;
  return null;
}

/** Web picker uses `blob:` object URLs — release them when the attachment is discarded. */
function revokeAttachmentUri(uri: string) {
  if (!uri.startsWith('blob:')) return;
  try {
    URL.revokeObjectURL(uri);
  } catch {
    // ignore
  }
}

const LABEL_VARIANT = 'Font Guardian App/Subtitle/Subtitle 12 Semibold';
const INPUT_VARIANT = 'Font Guardian App/Subtitle/Subtitle 14 Regular';

type ReportIssueSheetProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
};

/** Uploads the picked media to Storage so the report carries shareable URLs, not local paths. */
async function uploadAttachments(uid: string, attachments: ReportAttachment[]): Promise<string[]> {
  const stamp = Date.now();

  return await Promise.all(
    attachments.map(async (attachment, index) => {
      const extension =
        attachment.name.split('.').pop() || (attachment.kind === 'video' ? 'mp4' : 'jpg');
      const { downloadUrl } = await uploadFileToStorage(
        attachment.uri,
        `bug-reports/${uid}/${stamp}-${index}.${extension}`,
        attachment.mime || undefined,
      );
      return downloadUrl;
    }),
  );
}

/**
 * Serializes the on-device log buffer + device info to Storage. Native-only — web has no
 * `@react-native-firebase/storage` build for this path, so we skip rather than fail the report.
 */
async function uploadDebugLogAttachment(): Promise<string | null> {
  if (Platform.OS === 'web') return null;
  const { downloadUrl } = await uploadLogs();
  return downloadUrl;
}

/**
 * Submits the report via POST /api/v1/bug-report (issue + optional description / attachment URLs),
 * uploading screenshots/recordings and the debug-log JSON in parallel. Backend errors surface
 * through the shared server-error popup, so we simply skip the success path on failure.
 */
function useReportIssueSubmit(attachments: ReportAttachment[], onSuccess: () => void) {
  const uid = useSessionStore(state => state.user?.uid);
  const { mutateAsync, isPending } = useSubmitBugReportMutation();
  const [uploading, setUploading] = useState(false);
  const { showGlobalLoading, hideGlobalLoading } = useUiStore(
    useShallow(state => ({
      showGlobalLoading: state.showGlobalLoading,
      hideGlobalLoading: state.hideGlobalLoading,
    })),
  );

  const submit = useCallback(
    async (values: GuardianReportIssueForm) => {
      try {
        showGlobalLoading();
        setUploading(true);
        const [mediaUrls, logUrl] = await Promise.all([
          uid && attachments.length > 0 ? uploadAttachments(uid, attachments) : Promise.resolve([]),
          uploadDebugLogAttachment(),
        ]);
        const uploaded = logUrl ? [...mediaUrls, logUrl] : mediaUrls;
        setUploading(false);

        await mutateAsync({
          issue: values.issue.trim(),
          description: values.description?.trim() || undefined,
          attachments: uploaded.length > 0 ? uploaded : undefined,
        });
        onSuccess();
        showAlert({
          title: strings.successTitle,
          message: strings.successMessage,
          buttons: [{ text: strings.ok }],
        });
      } catch {
        // Upload/backend errors are surfaced by the shared error interceptors.
      } finally {
        setUploading(false);
        hideGlobalLoading();
      }
    },
    [attachments, hideGlobalLoading, mutateAsync, onSuccess, showGlobalLoading, uid],
  );

  return { submit, submitting: isPending || uploading };
}

/** Read-only identity field (First / Last name, Email) — prefilled from the account (BR-01). */
function AccountField({
  control,
  name,
  label,
  maxLength,
}: {
  control: Control<GuardianReportIssueForm>;
  name: 'firstName' | 'lastName' | 'email';
  label: string;
  maxLength?: number;
}) {
  const styles = useStyles();
  const { scale } = useScaling();

  return (
    <Controller
      control={control}
      name={name}
      render={({ field: { value }, fieldState: { error } }) => (
        <FormInput
          label={label}
          required
          disabled
          uppercaseLabel={false}
          labelVariant={LABEL_VARIANT}
          inputVariant={INPUT_VARIANT}
          labelGap={scale(2)}
          labelStyle={styles.fieldLabel}
          inputStyle={styles.fieldText}
          contentStyle={styles.fieldContent}
          value={value}
          status={error?.message ? 'error' : 'default'}
          errorMessage={error?.message}
          placeholder={label}
          maxLength={maxLength}
        />
      )}
    />
  );
}

/**
 * "Report an issue" bottom sheet, opened from the Settings list on the Guardian Profile screen.
 * Swiping down or tapping the backdrop closes it without submitting (Scenario 4).
 */
export function ReportIssueSheet({ open, onOpenChange }: ReportIssueSheetProps) {
  const styles = useStyles();
  const { scale, isTablet } = useScaling();
  const { control, handleSubmit } = useGuardianReportIssueForm(open);
  const pickAttachments = useReportAttachmentPicker();

  // Attachments live outside the form (they're media, not validated fields); clear them whenever
  // the sheet opens so a dismissed draft doesn't leak into the next report.
  const [attachments, setAttachments] = useState<ReportAttachment[]>([]);
  const [previewIndex, setPreviewIndex] = useState<number | null>(null);
  const [attachmentError, setAttachmentError] = useState<string | null>(null);
  useEffect(() => {
    if (open) {
      setAttachments(current => {
        current.forEach(item => revokeAttachmentUri(item.uri));
        return [];
      });
      setPreviewIndex(null);
      setAttachmentError(null);
    }
  }, [open]);

  const handlePick = useCallback(async () => {
    const options = getReportAttachmentPickerOptions(attachments);
    if (options.maxFiles <= 0) return;

    const picked = await pickAttachments(options);
    if (picked.length === 0) return;

    // Keep the selection in the strip (web OS dialog can't cap counts/sizes) and fail
    // validation — SC7–SC9 size copy + limit copy — until the guardian fixes the list.
    // Native uses the same path so size messages stay consistent across platforms.
    const merged = mergeReportAttachments(attachments, picked);
    setAttachments(merged);
    setAttachmentError(attachmentErrorMessage(getReportAttachmentValidationIssue(merged)));
  }, [attachments, pickAttachments]);

  const handleRemove = useCallback((uri: string) => {
    revokeAttachmentUri(uri);
    setAttachments(current => {
      const next = current.filter(item => item.uri !== uri);
      setAttachmentError(attachmentErrorMessage(getReportAttachmentValidationIssue(next)));
      setPreviewIndex(prev => {
        if (prev == null) return null;
        const removedAt = current.findIndex(item => item.uri === uri);
        if (removedAt < 0) return prev;
        if (next.length === 0) return null;
        if (prev > removedAt) return prev - 1;
        if (prev >= next.length) return next.length - 1;
        return prev;
      });
      return next;
    });
  }, []);

  const closePreview = useCallback(() => setPreviewIndex(null), []);
  const previewOpen = previewIndex != null;

  const close = useCallback(() => onOpenChange(false), [onOpenChange]);
  const { submit, submitting } = useReportIssueSubmit(attachments, close);
  const onSubmit = useCallback(() => handleSubmit(submit)(), [handleSubmit, submit]);

  return (
    <SheetModal open={open} onOpenChange={onOpenChange}>
      <SheetContent
        closeOnBackdropPress
        closeOnBackPress={!previewOpen}
        enableDragToClose={!submitting && !previewOpen}
        tabletWidth={scale(420)}
        style={isTablet ? styles.sheet : [styles.sheet, styles.sheetPhone]}
        overlay={
          previewIndex != null ? (
            <ReportAttachmentPreviewModal
              attachments={attachments}
              initialIndex={previewIndex}
              onClose={closePreview}
            />
          ) : undefined
        }>
        <Stack style={styles.root}>
          <KeyboardAwareScrollView
            style={styles.bodyScroll}
            showsVerticalScrollIndicator={false}
            keyboardShouldPersistTaps="handled"
            contentContainerStyle={styles.body}
            bottomOffset={scale(12)}
            extraKeyboardSpace={scale(12)}>
            <Text variant="Font Guardian App/Heading/Heading H6 18px" style={styles.title}>
              {strings.title}
            </Text>

            {/* Name — prefilled from the account, not editable (BR-01) */}
            <Stack direction="row" gap={scale(12)} align="flex-start">
              <Stack flex={1}>
                <AccountField control={control} name="firstName" label={strings.firstNameLabel} />
              </Stack>
              <Stack flex={1}>
                <AccountField control={control} name="lastName" label={strings.lastNameLabel} />
              </Stack>
            </Stack>

            <AccountField
              control={control}
              name="email"
              label={strings.emailLabel}
              maxLength={MAX_EMAIL_LENGTH}
            />

            {/* Issue — the only field the guardian must fill (BR-02) */}
            <Controller
              control={control}
              name="issue"
              render={({ field: { value, onChange, onBlur }, fieldState: { error } }) => (
                <FormInput
                  label={strings.issueLabel}
                  required
                  uppercaseLabel={false}
                  labelVariant={LABEL_VARIANT}
                  inputVariant={INPUT_VARIANT}
                  labelGap={scale(2)}
                  labelStyle={styles.fieldLabel}
                  inputStyle={styles.fieldText}
                  contentStyle={styles.fieldContent}
                  value={value}
                  onChangeText={onChange}
                  onBlur={onBlur}
                  status={error?.message ? 'error' : 'default'}
                  errorMessage={error?.message}
                  placeholder={strings.issuePlaceholder}
                  maxLength={MAX_REPORT_ISSUE_LENGTH}
                />
              )}
            />

            {/* Screenshots / recordings — up to MAX_REPORT_ATTACHMENTS, previewed before sending */}
            <ReportAttachmentField
              attachments={attachments}
              onPick={handlePick}
              onRemove={handleRemove}
              onPreview={setPreviewIndex}
              errorMessage={attachmentError}
              disabled={submitting}
            />

            {/* Description — optional detail (BR-02). maxLength blocks most devices; yup
                still catches paste / platforms that allow typing past 500. */}
            <Controller
              control={control}
              name="description"
              render={({ field: { value, onChange, onBlur }, fieldState: { error } }) => (
                <FormInput
                  label={strings.descriptionLabel}
                  required={false}
                  uppercaseLabel={false}
                  labelVariant={LABEL_VARIANT}
                  inputVariant={INPUT_VARIANT}
                  labelGap={scale(2)}
                  labelStyle={styles.fieldLabel}
                  inputStyle={styles.descriptionText}
                  contentStyle={styles.descriptionContent}
                  value={value}
                  onChangeText={onChange}
                  onBlur={onBlur}
                  status={error?.message ? 'error' : 'default'}
                  errorMessage={error?.message}
                  placeholder={strings.descriptionPlaceholder}
                  multiline
                  numberOfLines={4}
                  maxLength={MAX_REPORT_DESCRIPTION_LENGTH}
                />
              )}
            />
          </KeyboardAwareScrollView>

          <Stack style={styles.footer}>
            <Button
              onPress={onSubmit}
              disabled={submitting || Boolean(attachmentError)}
              style={styles.submitButton}>
              <Button.Text variant="Font Guardian App/Subtitle/Subtitle 14 Semibold">
                {strings.submit}
              </Button.Text>
            </Button>
          </Stack>
        </Stack>
      </SheetContent>
    </SheetModal>
  );
}

const useStyles = createStyles(({ scale, isTablet }) => ({
  sheet: { backgroundColor: 'white' },
  // Phone bottom-sheet sizes to content; tablet side-sheet fills height so the footer can pin.
  sheetPhone: { maxHeight: '90%' },
  root: { flex: isTablet ? 1 : undefined },
  bodyScroll: { flex: isTablet ? 1 : undefined },
  body: {
    paddingHorizontal: scale(20),
    paddingTop: scale(20),
    paddingBottom: scale(12),
    gap: scale(12),
  },
  title: {
    color: guardianColors['10'],
    textAlign: 'center',
    marginBottom: scale(4),
  },
  fieldLabel: { color: guardianColors['10'] },
  fieldText: { color: guardianColors['10'], marginLeft: 0 },
  fieldContent: {
    borderColor: guardianColors['3'],
    borderRadius: scale(8),
    height: scale(48),
    paddingLeft: scale(12),
    paddingRight: scale(12),
  },
  descriptionContent: {
    borderColor: guardianColors['3'],
    borderRadius: scale(8),
    height: scale(120),
    alignItems: 'flex-start',
    paddingLeft: scale(12),
    paddingRight: scale(12),
    paddingVertical: scale(8),
  },
  descriptionText: {
    color: guardianColors['10'],
    marginLeft: 0,
    height: '100%',
    textAlignVertical: 'top',
  },
  footer: {
    paddingHorizontal: scale(20),
    paddingTop: scale(12),
    paddingBottom: scale(12),
  },
  submitButton: {
    height: scale(48),
    borderRadius: scale(12),
  },
}));

export default ReportIssueSheet;
